import type { PrimaryIndustrySlug } from '@/constants/industries';
import { PRIMARY_INDUSTRIES } from '@/constants/industries';
import { find } from 'lodash';
import { getReadableCountry } from '@/utilities/markets';
import { pluralise } from '@/utilities/pluralise';
import { DEFAULT_INDUSTRY } from '../constants';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';

const CRAWLABLE_COMPANY_COUNT = 2;

type getIsIndexableProps = {
  market?: PrimaryCountryISOElseGlobal;
  industry?: string;
  page: number;
};

export const getIsIndexable = ({ industry, page }: getIsIndexableProps) => {
  const isDefaultIndustry = industry === DEFAULT_INDUSTRY || !industry;

  return (
    // Avoid indexing non-default industries + any pages 2+
    page === 1 && isDefaultIndustry
  );
};

export function useMetaData({
  name,
  market,
  industry,
  page,
  totalRecords,
  hasHiddenCountry,
}: {
  name: string;
  market: PrimaryCountryISOElseGlobal;
  industry: string;
  page: number;
  totalRecords: number;
  hasHiddenCountry?: boolean;
}) {
  const demonymCountry = hasHiddenCountry ? '' : getReadableCountry(market);
  const selectedIndustryItem = find(PRIMARY_INDUSTRIES, {
    slug: industry as PrimaryIndustrySlug,
  });

  const readableIndustryLabel = selectedIndustryItem
    ? selectedIndustryItem.label
    : 'Any';

  const title = `${demonymCountry}${
    readableIndustryLabel !== 'Any' ? ` ${readableIndustryLabel}` : ''
  } ${name}`;

  const paginatedName = page > 1 ? `Page ${page} - ${name}` : name;
  const documentTitle = `${
    totalRecords > 0 ? `${totalRecords} ` : ''
  }${demonymCountry}${
    readableIndustryLabel !== 'Any' ? ` ${readableIndustryLabel}` : ''
  } ${pluralise(
    totalRecords,
    'Stock',
    'Stocks'
  )} - ${paginatedName} - Investing Ideas`;

  const isIndexable = getIsIndexable({ industry, page });
  const allowCrawlers = totalRecords >= CRAWLABLE_COMPANY_COUNT && isIndexable;

  return {
    title,
    documentTitle,
    demonymCountry,
    allowCrawlers,
  };
}
